export const NOTICE_DISCLOSURE_SCHEMA_VERSION = 1;
export const NOTICE_DISCLOSURE_IDS = Object.freeze([
  "guide-translation-help",
  "calculator-data-quality",
  "map-zone-pane",
]);

const NOTICE_DISCLOSURE_ID_SET = new Set(NOTICE_DISCLOSURE_IDS);
const PROJECTION_FIELDS = Object.freeze([
  "canPersist",
  "kind",
  "noticeId",
  "open",
  "persisted",
  "revision",
  "schemaVersion",
  "warning",
]);
const PROJECTION_KINDS = new Set([
  "conflict",
  "invalid",
  "loading",
  "missing",
  "pending",
  "ready",
  "unavailable",
  "uninitialized",
  "unsupportedVersion",
]);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactFields(value, fields) {
  const actual = Object.keys(value).sort();
  const expected = [...fields].sort();
  return actual.length === expected.length
    && actual.every((field, index) => field === expected[index]);
}

export function requireNoticeDisclosureId(value) {
  if (
    typeof value !== "string"
    || value !== value.trim()
    || !NOTICE_DISCLOSURE_ID_SET.has(value)
  ) {
    throw new TypeError("Notice disclosure ID is not supported by the v1 contract.");
  }
  return value;
}

export function optionalNoticeDisclosureId(value) {
  if (value === null || value === undefined || value === "") return null;
  return requireNoticeDisclosureId(value);
}

export function parseNoticeDisclosureProjectionV1(value) {
  if (!isRecord(value) || !hasExactFields(value, PROJECTION_FIELDS)) {
    throw new TypeError("Notice disclosure projection fields must match v1.");
  }
  if (value.schemaVersion !== NOTICE_DISCLOSURE_SCHEMA_VERSION) {
    throw new TypeError("Notice disclosure projection schema version is not supported.");
  }
  const noticeId = requireNoticeDisclosureId(value.noticeId);
  if (!PROJECTION_KINDS.has(value.kind)) {
    throw new TypeError("Notice disclosure projection kind is not supported.");
  }
  if (
    value.revision !== null
    && (!Number.isSafeInteger(value.revision) || value.revision <= 0)
  ) {
    throw new TypeError("Notice disclosure projection revision must be null or positive.");
  }
  if (value.open !== null && typeof value.open !== "boolean") {
    throw new TypeError("Notice disclosure projection open value must be boolean or null.");
  }
  if (typeof value.persisted !== "boolean" || typeof value.canPersist !== "boolean") {
    throw new TypeError("Notice disclosure projection flags must be boolean.");
  }
  if (value.warning !== null && value.warning !== "legacyCleanupUnavailable") {
    throw new TypeError("Notice disclosure projection warning is not supported.");
  }
  return Object.freeze({
    schemaVersion: NOTICE_DISCLOSURE_SCHEMA_VERSION,
    noticeId,
    kind: value.kind,
    revision: value.revision,
    open: value.open,
    persisted: value.persisted,
    canPersist: value.canPersist,
    warning: value.warning,
  });
}

export function noticeDisclosureChangeDetail({
  noticeId,
  open,
  expectedRevision,
}) {
  const exactNoticeId = requireNoticeDisclosureId(noticeId);
  if (typeof open !== "boolean") {
    throw new TypeError("Notice disclosure open command must be boolean.");
  }
  if (
    expectedRevision !== null
    && (!Number.isSafeInteger(expectedRevision) || expectedRevision <= 0)
  ) {
    throw new TypeError("Notice disclosure expected revision must be null or positive.");
  }
  return Object.freeze({
    noticeId: exactNoticeId,
    open,
    expectedRevision,
  });
}
